import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import { Sparkles, MessageSquare, Send, X, Check, ChevronRight, ChevronLeft, ListChecks, CheckCircle2, XCircle, Clock, CalendarClock, Circle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";

export default function Campaigns() {
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [treatments, setTreatments] = useState([]);
  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/treatments").then((r) => setTreatments(r.data.filter((t) => t.schedulable !== false))).catch(() => {});
  }, []);
  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-display mb-1">Campagnes</h1>
      <p className="text-ink-muted text-sm mb-6">Bied vrije plekken aan of stuur een SMS naar je klanten.</p>
      <Tabs defaultValue="offer">
        <TabsList className="mb-6">
          <TabsTrigger value="offer" data-testid="tab-offer"><Sparkles className="w-4 h-4 mr-1.5" /> Plekje aanbieden</TabsTrigger>
          <TabsTrigger value="overview" data-testid="tab-overview"><ListChecks className="w-4 h-4 mr-1.5" /> Overzicht</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms"><MessageSquare className="w-4 h-4 mr-1.5" /> Vrije SMS</TabsTrigger>
        </TabsList>
        <TabsContent value="offer"><OfferWizard customers={customers} treatments={treatments} params={params} /></TabsContent>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="sms"><SmsForm customers={customers} /></TabsContent>
      </Tabs>
    </div>
  );
}

function MultiAutocomplete({ customers, selected, setSelected }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const matches = useMemo(() => {
    const ql = q.toLowerCase().trim();
    return customers.filter((c) => !selected.includes(c.id) &&
      (!ql || c.name.toLowerCase().includes(ql) || (c.phone || "").includes(ql) || (c.email || "").toLowerCase().includes(ql))).slice(0, 6);
  }, [q, customers, selected]);
  const chosen = customers.filter((c) => selected.includes(c.id));
  return (
    <div className="relative" ref={ref}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {chosen.map((c) => (
          <span key={c.id} className="inline-flex items-center gap-1 bg-brand-light text-brand text-xs rounded-full px-2.5 py-1" data-testid={`chip-${c.id}`}>
            {c.name}<button onClick={() => setSelected(selected.filter((x) => x !== c.id))}><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid="customer-autocomplete"
        placeholder="Zoek klant op naam, telefoon of e-mail..." className="rounded-xl border-[#E8E2D9]" />
      {open && matches.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-[#E8E2D9] rounded-xl shadow-lg overflow-hidden">
          {matches.map((c) => (
            <button key={c.id} onMouseDown={() => { setSelected([...selected, c.id]); setQ(""); }}
              data-testid={`ac-option-${c.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-cream-subtle flex justify-between">
              <span>{c.name}</span><span className="text-ink-muted text-xs">{c.phone}</span>
            </button>
          ))}
        </div>
      )}
      <div className="text-xs text-ink-muted mt-1">{selected.length} klant(en) geselecteerd</div>
    </div>
  );
}

function OfferWizard({ customers, treatments, params }) {
  const [step, setStep] = useState(1);
  const [date, setDate] = useState(params.get("date") || "");
  const [start, setStart] = useState(params.get("start") || "14:00");
  const [end, setEnd] = useState(params.get("end") || "");
  const [tids, setTids] = useState([]);
  const [cids, setCids] = useState([]);
  const [deadlineDate, setDeadlineDate] = useState("");
  const [deadlineTime, setDeadlineTime] = useState("12:00");
  const [comment, setComment] = useState("Hey, er is toevallig een plekje vrij en ik dacht aan jou. Past dit binnen jouw schema? Laat het gerust even weten!");
  const [busy, setBusy] = useState(false);

  const tNames = treatments.filter((t) => tids.includes(t.id)).map((t) => t.name).join(", ");
  const dayTxt = date ? format(parseISO(`${date}T00:00:00`), "EEEE d MMMM", { locale: nl }) : "…";
  const isRange = end && end !== start;
  const windowTxt = isRange ? `${dayTxt} tussen ${start} en ${end}` : `${dayTxt} om ${start}`;
  const preview = `Hallo [naam],\n\nEr is een plekje vrij op ${windowTxt} voor: ${tNames || "[behandeling]"}.\n\n${comment}\n\n${cids.length > 1 ? "Wie het eerst reageert, heeft de plek. " : ""}Reageer via de knop in dit bericht.\n\nGroetjes, Nelleke`;

  const next = () => {
    if (step === 1 && !date) return toast.error("Kies een datum en tijdvenster.");
    if (step === 1 && end && end <= start) return toast.error("De 'tot'-tijd moet na de 'van'-tijd liggen (of laat leeg voor een vaste tijd).");
    if (step === 2 && !tids.length) return toast.error("Kies minstens één behandeling.");
    if (step === 3 && (!cids.length || !deadlineDate)) return toast.error("Kies klanten en een reageer-deadline.");
    setStep(step + 1);
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/offers", {
        treatment_ids: tids, customer_ids: cids,
        window_start: `${date}T${start}:00`, window_end: `${date}T${end || start}:00`,
        deadline: `${deadlineDate}T${deadlineTime}:00`, comment, notify: true,
      });
      toast.success(`Aanbod verstuurd naar ${r.data.recipients} klant(en).`);
      setStep(1); setTids([]); setCids([]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const steps = ["Wanneer", "Behandeling", "Klanten", "Bericht"];
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6">
      <div className="flex items-center gap-2 mb-6">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 text-xs ${step === i + 1 ? "text-brand font-medium" : step > i + 1 ? "text-ink-muted" : "text-ink-muted/50"}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step > i + 1 ? "bg-brand text-white" : step === i + 1 ? "bg-brand-light text-brand" : "bg-cream-subtle"}`}>{step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}</span>
              <span className="hidden sm:inline">{s}</span>
            </div>
            {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-ink-muted/40" />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4" data-testid="wizard-step-1">
          <p className="text-sm text-ink-muted">Voor welk tijdvenster bied je een plekje aan? (vanuit de agenda voorgevuld)</p>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-sm mb-1 block">Datum</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="offer-date" className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm mb-1 block">Van</Label><Input type="time" step="900" value={start} onChange={(e) => setStart(e.target.value)} data-testid="offer-start" className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm mb-1 block">Tot <span className="text-ink-muted font-normal">(optioneel)</span></Label><Input type="time" step="900" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="offer-end" placeholder="vaste tijd" className="rounded-xl border-[#E8E2D9]" /></div>
          </div>
          <p className="text-xs text-ink-muted">Laat <strong>Tot</strong> leeg voor een vaste tijd (bijv. "om 12:15"). Vul je een eindtijd in, dan mag de klant zelf een moment binnen dat venster kiezen.</p>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-3" data-testid="wizard-step-2">
          <p className="text-sm text-ink-muted">Welke behandeling(en) mogen ze kiezen?</p>
          <div className="flex flex-wrap gap-2">
            {treatments.map((t) => (
              <button key={t.id} type="button" onClick={() => setTids(tids.includes(t.id) ? tids.filter((x) => x !== t.id) : [...tids, t.id])}
                data-testid={`offer-treatment-${t.id}`}
                className={`text-xs rounded-full px-3 py-1.5 border ${tids.includes(t.id) ? "bg-brand text-white border-brand" : "border-[#E8E2D9] text-ink-muted"}`}>{t.name} · {t.duration_min}m</button>
            ))}
          </div>
        </div>
      )}
      {step === 3 && (
        <div className="space-y-4" data-testid="wizard-step-3">
          <div><Label className="text-sm mb-2 block">Aan welke klanten?</Label><MultiAutocomplete customers={customers} selected={cids} setSelected={setCids} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm mb-1 block">Reageren vóór (datum)</Label><Input type="date" value={deadlineDate} onChange={(e) => setDeadlineDate(e.target.value)} data-testid="offer-deadline-date" className="rounded-xl border-[#E8E2D9]" /></div>
            <div><Label className="text-sm mb-1 block">Tijd</Label><Input type="time" step="900" value={deadlineTime} onChange={(e) => setDeadlineTime(e.target.value)} data-testid="offer-deadline-time" className="rounded-xl border-[#E8E2D9]" /></div>
          </div>
        </div>
      )}
      {step === 4 && (
        <div className="space-y-3" data-testid="wizard-step-4">
          <Label className="text-sm block">Jouw persoonlijke tekst (pas gerust aan)</Label>
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} data-testid="offer-comment" className="rounded-xl border-[#E8E2D9]" />
          <Label className="text-sm block mt-2">Voorbeeld van het bericht</Label>
          <div className="rounded-xl bg-cream-subtle/60 border border-[#E8E2D9] p-4 text-sm whitespace-pre-line" data-testid="offer-preview">{preview}</div>
          <p className="text-xs text-ink-muted">Verstuurd naar {cids.length} klant(en) via e-mail/SMS. First come, first served.</p>
        </div>
      )}

      <div className="flex justify-between pt-6 mt-4 border-t border-[#F2EBE1]">
        <Button variant="outline" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="rounded-full border-[#E8E2D9]"><ChevronLeft className="w-4 h-4 mr-1" /> Terug</Button>
        {step < 4
          ? <Button onClick={next} data-testid="wizard-next" className="rounded-full bg-brand hover:bg-brand-hover text-white">Volgende <ChevronRight className="w-4 h-4 ml-1" /></Button>
          : <Button onClick={send} disabled={busy} data-testid="offer-send-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white"><Send className="w-4 h-4 mr-2" /> {busy ? "Versturen..." : "Aanbod versturen"}</Button>}
      </div>
    </div>
  );
}

function StatusBadge({ responded, isWinner }) {
  const map = {
    yes: { icon: <CheckCircle2 className="w-3.5 h-3.5" />, label: isWinner ? "Ja · plek gepakt" : "Ja", cls: "bg-green-50 text-green-700 border-green-200" },
    no: { icon: <XCircle className="w-3.5 h-3.5" />, label: "Nee, past niet", cls: "bg-[#FBEBEB] text-[#9A5B5B] border-[#EBD3D3]" },
    late: { icon: <Clock className="w-3.5 h-3.5" />, label: "Net te laat", cls: "bg-[#FBEBD2] text-[#9A5B12] border-[#E9D2A8]" },
    alt: { icon: <CalendarClock className="w-3.5 h-3.5" />, label: "Ja, ander moment", cls: "bg-brand-light text-brand border-[#E3C9C9]" },
  };
  const s = map[responded] || { icon: <Circle className="w-3.5 h-3.5" />, label: "Nog geen reactie", cls: "bg-cream-subtle text-ink-muted border-[#E8E2D9]" };
  return <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 border ${s.cls}`}>{s.icon}{s.label}</span>;
}

function OverviewTab() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/offers").then((r) => setOffers(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const confirmOffer = async (o) => {
    setBusy(o.id);
    try {
      await api.post(`/admin/appointments/${o.appointment_id}/confirm-offer`);
      toast.success("Afspraak bevestigd.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const windowTxt = (o) => {
    const s = parseISO(o.window_start), e = parseISO(o.window_end);
    const day = format(s, "EEEE d MMMM", { locale: nl });
    return o.window_start === o.window_end
      ? `${day} om ${format(s, "HH:mm")}`
      : `${day} · ${format(s, "HH:mm")}–${format(e, "HH:mm")}`;
  };

  if (loading) return <div className="text-sm text-ink-muted p-6">Laden...</div>;
  if (!offers.length) return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 text-center text-sm text-ink-muted" data-testid="overview-empty">
      Je hebt nog geen plekjes aangeboden. Zodra je een uitvraag verstuurt, zie je hier per klant wie heeft gereageerd.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="overview-list">
      {offers.map((o) => {
        const recs = o.recipients || [];
        const winnerId = o.claimed_by;
        const counts = recs.reduce((a, r) => { const k = r.responded || "none"; a[k] = (a[k] || 0) + 1; return a; }, {});
        return (
          <div key={o.id} className="bg-white rounded-2xl border border-[#E8E2D9] p-5" data-testid={`overview-offer-${o.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="font-medium capitalize">{windowTxt(o)}</div>
                <div className="text-sm text-ink-muted">{(o.treatment_names || []).join(", ")}</div>
              </div>
              <span className={`text-xs rounded-full px-2.5 py-1 border whitespace-nowrap ${o.status === "filled" ? "bg-green-50 text-green-700 border-green-200" : "bg-cream-subtle text-ink-muted border-[#E8E2D9]"}`}>
                {o.status === "filled" ? "Vergeven" : "Open"}
              </span>
            </div>
            {o.status === "filled" && o.claimed_start && (() => {
              const w = recs.find((r) => r.customer_id === winnerId);
              const ti = (o.treatment_ids || []).indexOf(o.claimed_treatment_id);
              const tname = ti >= 0 ? (o.treatment_names || [])[ti] : (o.treatment_names || []).join(", ");
              return (
                <div className="mb-2 text-sm" data-testid={`overview-winner-${o.id}`}>
                  <span className="font-medium">{w ? w.name : "Klant"}</span>
                  <span className="text-ink-muted"> koos {tname} · {format(parseISO(o.claimed_start), "EEE d MMM · HH:mm", { locale: nl })}</span>
                </div>
              );
            })()}
            {o.status === "filled" && (
              o.appointment_status === "offer_pending" ? (
                <button onClick={() => confirmOffer(o)} disabled={busy === o.id} data-testid={`confirm-offer-${o.id}`}
                  className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-brand hover:bg-brand-hover text-white text-xs px-3 py-1.5 disabled:opacity-60">
                  <Check className="w-3.5 h-3.5" /> {busy === o.id ? "Bevestigen..." : "Afspraak definitief bevestigen"}
                </button>
              ) : o.appointment_status === "confirmed" ? (
                <span className="mb-3 inline-flex items-center gap-1.5 text-xs text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> Afspraak bevestigd</span>
              ) : null
            )}
            <div className="flex flex-wrap gap-1.5 text-xs text-ink-muted mb-3">
              <span>{recs.length} aangeschreven</span>
              {counts.yes ? <span>· {counts.yes} ja</span> : null}
              {counts.alt ? <span>· {counts.alt} ander moment</span> : null}
              {counts.no ? <span>· {counts.no} nee</span> : null}
              {counts.late ? <span>· {counts.late} te laat</span> : null}
              {counts.none ? <span>· {counts.none} geen reactie</span> : null}
            </div>
            <div className="divide-y divide-[#F2EBE1] border-t border-[#F2EBE1]">
              {recs.map((r) => (
                <div key={r.token} className="flex items-center justify-between gap-3 py-2" data-testid={`overview-rec-${r.customer_id}`}>
                  <span className="text-sm">{r.name}</span>
                  <StatusBadge responded={r.responded} isWinner={winnerId && r.customer_id === winnerId} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SmsForm({ customers }) {
  const [audience, setAudience] = useState("selected");
  const [cids, setCids] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return toast.error("Typ een bericht.");
    if (audience === "selected" && !cids.length) return toast.error("Kies klanten of stuur naar iedereen.");
    setBusy(true);
    try {
      const r = await api.post("/admin/sms-blast", { audience, customer_ids: cids, text });
      toast.success(`SMS verstuurd naar ${r.data.sms_sent} van ${r.data.targets} klant(en).`);
      setText("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#E8E2D9] p-6 space-y-5">
      <div className="flex gap-2">
        <button type="button" onClick={() => setAudience("all")} data-testid="sms-audience-all" className={`text-sm rounded-full px-4 py-2 border ${audience === "all" ? "bg-brand text-white border-brand" : "border-[#E8E2D9]"}`}>Alle klanten</button>
        <button type="button" onClick={() => setAudience("selected")} data-testid="sms-audience-selected" className={`text-sm rounded-full px-4 py-2 border ${audience === "selected" ? "bg-brand text-white border-brand" : "border-[#E8E2D9]"}`}>Selectie</button>
      </div>
      {audience === "selected" && <div><Label className="text-sm mb-2 block">Klanten</Label><MultiAutocomplete customers={customers} selected={cids} setSelected={setCids} /></div>}
      <div><Label className="text-sm mb-1 block">Bericht</Label><Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} data-testid="sms-text" placeholder="Typ hier je SMS-bericht..." className="rounded-xl border-[#E8E2D9]" /><p className="text-xs text-ink-muted mt-1">{text.length} tekens · alleen klanten met SMS aan ontvangen dit.</p></div>
      <Button onClick={send} disabled={busy} data-testid="sms-send-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white"><Send className="w-4 h-4 mr-2" /> {busy ? "Versturen..." : "SMS versturen"}</Button>
    </div>
  );
}
